import { Box, Menu, Portal, Text } from '@chakra-ui/react';
import { MdDownload } from 'react-icons/md';
import { HiArrowDownTray } from 'react-icons/hi2';

/**
 * A single export/download action.
 *
 * Canonical item type shared by `ExportMenu` and `TableOptions`'
 * `exportMenu` prop — declared once here; `TableOptions` re-exports it.
 */
export interface ExportMenuItem {
  /** Label displayed in the menu */
  label: string;
  /** Callback when item is clicked */
  onClick: () => void;
  /** Optional data-e2e attribute for testing */
  e2eTitle?: string;
  /** Optional data-e2e attribute for testing (alternative) */
  'data-e2e'?: string;
}

export interface ExportMenuProps {
  /** Array of export options */
  items: ExportMenuItem[];
  /** Data attribute for e2e testing */
  e2eTitle?: string;
  /** Optional accessibility callback for screen readers */
  onSpeak?: (text: string) => void;
  /**
   * Visual treatment of the trigger button.
   * - `primary` (default): filled brand button with a white download icon —
   *   the standalone report-page look.
   * - `toolbar`: bordered surface button matching the other `TableOptions`
   *   toolbar controls (search / filter). Used by `TableOptions`' `exportMenu`
   *   prop, which delegates its rendering here.
   */
  visual?: 'primary' | 'toolbar';
}

/**
 * Standardized export/download menu component.
 *
 * When a single item is provided, renders as a button that triggers the action directly.
 * When multiple items are provided, renders as a dropdown menu.
 *
 * @example
 * ```tsx
 * // Single export option (renders as button)
 * <ExportMenu
 *   items={[{ label: 'Export CSV', onClick: handleExport }]}
 *   e2eTitle="exportButton"
 * />
 *
 * // Multiple export options (renders as dropdown)
 * <ExportMenu
 *   items={[
 *     { label: 'User Summary', onClick: handleSummaryExport },
 *     { label: 'User List', onClick: handleListExport },
 *   ]}
 *   e2eTitle="exportMenu"
 *   onSpeak={speakAccessibility}
 * />
 * ```
 *
 * @useFor the canonical "Export" dropdown on a table or report page
 *   — Download CSV / Print / Share / Copy-to-clipboard menu.
 *
 *   Example in-tree: the SMS Delivery Metrics page
 *   (`apps/aegix-sms-react/src/pages/DeliveryMetrics.tsx`) renders CSV + PDF
 *   export options.
 *
 *   `items` accepts heterogeneous action shapes (download, print,
 *   custom) so app code doesn't have to compose menus by hand. A
 *   single item renders as a direct-action download button (no
 *   dropdown); an empty `items` array renders nothing.
 *
 *   `TableOptions`' `exportMenu` prop delegates here with
 *   `visual="toolbar"`, which swaps the filled brand trigger
 *   for a bordered surface button that matches the toolbar's other
 *   controls. Don't re-implement the export dropdown inline.
 */
export function ExportMenu({ items, e2eTitle, onSpeak, visual = 'primary' }: ExportMenuProps) {
  if (items.length === 0) {
    return null;
  }

  const handleSpeak = (text: string) => {
    onSpeak?.(text.toLowerCase());
  };

  const isToolbar = visual === 'toolbar';

  // The `toolbar` variant is a bordered surface button that sits flush with
  // the TableOptions toolbar's search/filter controls.
  const toolbarTriggerProps = {
    bg: 'surface.primary',
    borderWidth: '1px',
    borderColor: 'border.line',
    borderRadius: '8px',
    px: 2.5,
    py: 2,
    color: 'text.primary',
    _hover: { bg: 'surface.secondary' },
  } as const;

  const triggerIcon = isToolbar ? (
    <HiArrowDownTray size={20} />
  ) : (
    <MdDownload size={22} color="white" />
  );

  // Single item: render as a simple button
  if (items.length === 1) {
    return (
      <Box
        as="button"
        aria-label={`Export ${items[0].label}`}
        data-e2e={e2eTitle || 'exportMenu'}
        cursor="pointer"
        display="flex"
        alignItems="center"
        justifyContent="center"
        onClick={() => {
          items[0].onClick();
          handleSpeak(items[0].label);
        }}
        {...(isToolbar
          ? toolbarTriggerProps
          : {
              bg: 'defaultSafety.primary',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              _hover: { opacity: 0.9 },
              _focusVisible: {
                outline: '2px solid',
                outlineColor: 'border.action',
                outlineOffset: '2px',
              },
            })}
      >
        {triggerIcon}
      </Box>
    );
  }

  // Multiple items: render as dropdown menu.
  // The trigger is a real <button> for BOTH visuals so it is
  // keyboard-focusable and operable (Enter/Space/ArrowDown open the menu);
  // every resting style is set explicitly below so no UA button styling
  // shows through.
  return (
    <Menu.Root positioning={{ sameWidth: false }}>
      <Menu.Trigger asChild>
        <Box
          as="button"
          aria-label="Export options"
          data-e2e={e2eTitle || 'exportMenu'}
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          {...(isToolbar
            ? toolbarTriggerProps
            : {
                bg: 'defaultSafety.primary',
                border: 'none',
                borderRadius: '8px',
                padding: '8px',
                _focusVisible: {
                  outline: '2px solid',
                  outlineColor: 'border.action',
                  outlineOffset: '2px',
                },
              })}
        >
          {triggerIcon}
        </Box>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            minWidth="150px"
            padding="0px"
            bg="surface.primary"
            border="1px solid"
            borderColor="border.secondary"
            borderRadius="md"
            overflow="hidden"
          >
            {items.map((item, index) => (
              <Menu.Item
                key={index}
                value={item.label}
                data-e2e={item['data-e2e'] || item.e2eTitle}
                data-speak={item.label}
                textTransform="capitalize"
                fontSize="14px"
                fontWeight={600}
                bg="surface.primary"
                color="text.primary"
                border="none"
                outline="none"
                borderRadius="none"
                onClick={() => {
                  item.onClick();
                  handleSpeak(item.label);
                }}
                _hover={{
                  outline: 'none',
                  bg: 'surface.secondary',
                  cursor: 'pointer',
                }}
                _focus={{
                  bg: 'surface.secondary',
                }}
                _focusVisible={{
                  outline: '2px solid var(--chakra-colors-border-action)',
                  outlineOffset: '-2px',
                }}
                py="10px"
                px="16px"
              >
                <Text>{item.label}</Text>
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
